import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { backupsAPI, domainsAPI } from '@hpanel/web-common/api/client'
import { useAuth } from '@hpanel/web-common/contexts/AuthContext'
import type { BackupResponse, Domain } from '@hpanel/web-common/api/types'
import { pollWithBackoff } from '../../components/Backups/pollWithBackoff'

type BackupScope = 'account' | 'domain'
type BackupKind = 'full' | 'files' | 'databases' | 'mail'

interface KindOption {
  value: BackupKind
  label: string
  description: string
  /** v1 only ships kind="full" for single-domain backups; the others
   *  are reserved in the model but the BackupService rejects them with
   *  422. Surface the options anyway so the user sees the roadmap. */
  comingSoon: boolean
}

const KIND_OPTIONS: KindOption[] = [
  {
    value: 'full',
    label: 'Full (files + databases + mail)',
    description:
      'Everything for the selected domain: home directory, databases (mysqldump), and Maildir.',
    comingSoon: false,
  },
  {
    value: 'files',
    label: 'Files only',
    description:
      'Just the home directory (public_html, configs, uploads). Faster, but databases and mail are skipped.',
    comingSoon: true,
  },
  {
    value: 'databases',
    label: 'Databases only',
    description: 'Just mysqldump output for each database the tenant owns.',
    comingSoon: true,
  },
  {
    value: 'mail',
    label: 'Mail only',
    description: 'Just the Maildir tree (incoming + saved messages).',
    comingSoon: true,
  },
]

const TERMINAL = new Set(['success', 'failed'])

/**
 * CreateBackupPage — route: /client/backups/new
 *
 * Replaces the prior dropdown-popup create-backup flow (per CLAUDE.md §15:
 * multi-step flows live on dedicated pages, not inline forms). Three steps:
 *
 *   1. Pick a domain (resource selection).
 *   2. Pick a backup kind (full / files / db / mail — v1 ships full only).
 *   3. Review + confirm, then POST /api/backups and poll to completion.
 *
 * On success, navigates back to /client/backups so the new backup row is
 * visible in the table.
 */
export const CreateBackupPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [domains, setDomains] = useState<Domain[]>([])
  const [backups, setBackups] = useState<BackupResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Account-scope is the default and recommended choice: one file
  // covers every domain + settings + mail + databases and is
  // restorable in one shot. Single-domain is the power-user option
  // for partial restores or off-site shipping of one site.
  const [selectedScope, setSelectedScope] = useState<BackupScope>('account')
  const [selectedDomainId, setSelectedDomainId] = useState<number | null>(null)
  const [selectedKind, setSelectedKind] = useState<BackupKind>('full')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        setLoading(true)
        setLoadError(null)
        const [ds, bs] = await Promise.all([
          domainsAPI.list(user?.id),
          backupsAPI.list(),
        ])
        if (cancelled) return
        setDomains(ds.domains)
        setBackups(bs)
      } catch (err) {
        if (cancelled) return
        console.error('CreateBackupPage: failed to load domains/backups', err)
        setLoadError(
          err instanceof Error
            ? err.message
            : 'Unable to load domains. Please try again.',
        )
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user?.id])

  // Only domains bound to a container can be backed up. The May 2026 bug
  // was here: the response schema dropped container_id so this filter
  // saw `undefined`, passed every domain through, and then resolved to
  // null in the submit guard. The schema now ships container_id, but
  // we still hard-filter so the dropdown can't surface a domain that
  // we know has nothing to back up.
  const backupableDomains = domains.filter(
    (d) => typeof d.container_id === 'number',
  )
  const selectedDomain =
    selectedDomainId !== null
      ? backupableDomains.find((d) => d.id === selectedDomainId) ?? null
      : null
  const selectedContainerId = selectedDomain?.container_id ?? null

  const activeBackup =
    selectedContainerId !== null
      ? backups.find(
          (b) =>
            b.container_id === selectedContainerId &&
            (b.status === 'pending' || b.status === 'running'),
        )
      : undefined
  const blockedByActive = activeBackup !== undefined

  const selectedKindOption = KIND_OPTIONS.find((k) => k.value === selectedKind)
  const kindUnavailable = selectedKindOption?.comingSoon ?? false

  const canSubmitAccount = !submitting && backupableDomains.length > 0
  const canSubmitDomain =
    !submitting &&
    !blockedByActive &&
    !kindUnavailable &&
    selectedContainerId !== null
  const canSubmit =
    selectedScope === 'account' ? canSubmitAccount : canSubmitDomain

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      if (selectedScope === 'account') {
        // Plan 20 P1: account backup is async on the backend. The
        // POST returns 201 immediately with status="pending"; the
        // background task does the work. We navigate to the list
        // right away — the BackupList polls in-progress rows and
        // surfaces the download button once status="success".
        await backupsAPI.createAccount()
      } else {
        if (selectedContainerId === null) return
        // Per-container backups are still synchronous in v1; the
        // POST blocks until done. Keep the brief wait so the user
        // doesn't navigate while a 4-second tar is in flight.
        const backup = await backupsAPI.create({
          container_id: selectedContainerId,
          kind: selectedKind,
        })
        if (!TERMINAL.has(backup.status)) {
          const signal = { cancelled: false }
          await pollWithBackoff<BackupResponse>(
            () => backupsAPI.get(backup.id),
            (r) => TERMINAL.has(r.status),
            () => undefined,
            signal,
          )
        }
      }

      navigate('/client/backups')
    } catch (err) {
      console.error('CreateBackupPage: backup creation failed', err)
      setSubmitError(
        err instanceof Error
          ? err.message
          : 'Backup creation failed. Please try again.',
      )
      setSubmitting(false)
    }
  }

  return (
    <div
      data-testid="create-backup-page"
      className="max-w-3xl mx-auto space-y-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to="/client/backups"
            className="text-sm text-primary-600 hover:underline"
            data-testid="create-backup-back-link"
          >
            ← Back to backups
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">
            Create a new backup
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
            Pick a domain and the backup scope. The backup is stored inside
            the site's container; download it after creation if you want a
            copy you can keep across account events.
          </p>
        </div>
      </div>

      {loadError && (
        <div
          data-testid="create-backup-load-error"
          className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-800 dark:text-red-200 rounded-lg p-3 text-sm"
        >
          {loadError}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          Loading domains…
        </p>
      ) : backupableDomains.length === 0 ? (
        <div
          data-testid="create-backup-no-domains"
          className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 text-yellow-900 dark:text-yellow-200 rounded-lg p-4 text-sm"
        >
          You don't have any domains with a backing container yet. Create a
          domain from{' '}
          <Link
            to="/client/domains"
            className="text-primary-600 underline"
          >
            Domain Management
          </Link>{' '}
          first, then come back here.
        </div>
      ) : (
        <>
          {/* Step 1 — pick the scope */}
          <section
            data-testid="create-backup-step-scope"
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-3"
          >
            <header>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Step 1 — Choose the backup scope
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Account backups bundle every domain you own (files,
                databases, mail) plus your account settings into ONE
                file you can restore in one shot.
              </p>
            </header>
            <ul className="space-y-2">
              {[
                {
                  value: 'account' as BackupScope,
                  label: 'Account backup (recommended)',
                  description: `All ${backupableDomains.length} of your domain${backupableDomains.length === 1 ? '' : 's'}, plus account settings, mail, and databases — in a single file. Use this for "give me everything back" recovery.`,
                },
                {
                  value: 'domain' as BackupScope,
                  label: 'Single domain backup',
                  description:
                    'Just one of your domains. Useful for off-site shipping of one site or for partial restore.',
                },
              ].map((opt) => {
                const isSelected = opt.value === selectedScope
                return (
                  <li key={opt.value}>
                    <label
                      className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/30'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="backup-scope"
                        value={opt.value}
                        checked={isSelected}
                        onChange={() => setSelectedScope(opt.value)}
                        data-testid={`create-backup-scope-${opt.value}`}
                        className="mt-1 text-primary-600"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {opt.label}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                          {opt.description}
                        </div>
                      </div>
                    </label>
                  </li>
                )
              })}
            </ul>
          </section>

          {/* Step 2 (domain scope only) — pick a domain */}
          {selectedScope === 'domain' && (
            <section
              data-testid="create-backup-step-domain"
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-3"
            >
              <header>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Step 2 — Choose a domain
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Each domain has its own container. The backup covers
                  whatever the container holds.
                </p>
              </header>
              <ul className="space-y-2">
                {backupableDomains.map((d) => {
                  const isSelected = d.id === selectedDomainId
                  return (
                    <li key={d.id}>
                      <label
                        className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
                          isSelected
                            ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/30'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="backup-domain"
                          value={d.id}
                          checked={isSelected}
                          onChange={() => setSelectedDomainId(d.id)}
                          data-testid={`create-backup-domain-${d.name}`}
                          className="text-primary-600"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {d.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            container #{d.container_id} · status {d.status}
                          </div>
                        </div>
                      </label>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}

          {/* Step 3 (domain scope only) — pick a kind */}
          {selectedScope === 'domain' && (
            <section
              data-testid="create-backup-step-kind"
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-3"
            >
              <header>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Step 3 — Choose what to include
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Full is recommended. Partial scopes are reserved for
                  a future release.
                </p>
              </header>
              <ul className="space-y-2">
                {KIND_OPTIONS.map((opt) => {
                  const isSelected = opt.value === selectedKind
                  return (
                    <li key={opt.value}>
                      <label
                        className={`flex items-start gap-3 p-3 border rounded-md transition-colors ${
                          opt.comingSoon
                            ? 'opacity-60 cursor-not-allowed border-gray-200 dark:border-gray-700'
                            : isSelected
                              ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/30 cursor-pointer'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer'
                        }`}
                      >
                        <input
                          type="radio"
                          name="backup-kind"
                          value={opt.value}
                          checked={isSelected}
                          onChange={() => setSelectedKind(opt.value)}
                          disabled={opt.comingSoon}
                          data-testid={`create-backup-kind-${opt.value}`}
                          className="mt-1 text-primary-600"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                            {opt.label}
                            {opt.comingSoon && (
                              <span className="text-xs uppercase tracking-wide bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                                Coming soon
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                            {opt.description}
                          </div>
                        </div>
                      </label>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}

          {/* Final step — review & submit */}
          <section
            data-testid="create-backup-step-review"
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-3"
          >
            <header>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selectedScope === 'account' ? 'Step 2 — Review & start' : 'Step 4 — Review & start'}
              </h2>
            </header>

            {selectedScope === 'domain' && blockedByActive && (
              <div
                data-testid="create-backup-already-running"
                className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 text-yellow-900 dark:text-yellow-200 rounded-md p-3 text-sm"
              >
                A backup is already {activeBackup?.status} for this domain
                (backup #{activeBackup?.id}). Wait for it to finish before
                starting another.
              </div>
            )}

            <dl className="text-sm">
              <div className="flex justify-between border-b border-gray-100 dark:border-gray-700 py-2">
                <dt className="text-gray-500 dark:text-gray-400">Scope</dt>
                <dd
                  data-testid="create-backup-review-scope"
                  className="font-medium text-gray-900 dark:text-gray-100"
                >
                  {selectedScope === 'account'
                    ? `Account backup (${backupableDomains.length} domain${backupableDomains.length === 1 ? '' : 's'})`
                    : 'Single domain'}
                </dd>
              </div>
              {selectedScope === 'domain' && (
                <>
                  <div className="flex justify-between border-b border-gray-100 dark:border-gray-700 py-2">
                    <dt className="text-gray-500 dark:text-gray-400">Domain</dt>
                    <dd
                      data-testid="create-backup-review-domain"
                      className="font-medium text-gray-900 dark:text-gray-100"
                    >
                      {selectedDomain?.name ?? '—'}
                    </dd>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 dark:border-gray-700 py-2">
                    <dt className="text-gray-500 dark:text-gray-400">
                      Container ID
                    </dt>
                    <dd
                      data-testid="create-backup-review-container"
                      className="font-medium text-gray-900 dark:text-gray-100"
                    >
                      {selectedContainerId ?? '—'}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2">
                    <dt className="text-gray-500 dark:text-gray-400">Contents</dt>
                    <dd
                      data-testid="create-backup-review-kind"
                      className="font-medium text-gray-900 dark:text-gray-100"
                    >
                      {selectedKindOption?.label ?? '—'}
                    </dd>
                  </div>
                </>
              )}
              {selectedScope === 'account' && (
                <div className="flex justify-between py-2">
                  <dt className="text-gray-500 dark:text-gray-400">Contents</dt>
                  <dd className="font-medium text-gray-900 dark:text-gray-100">
                    All domains + settings + databases + mail
                  </dd>
                </div>
              )}
            </dl>

            {submitError && (
              <div
                data-testid="create-backup-submit-error"
                className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-800 dark:text-red-200 rounded-md p-3 text-sm"
              >
                {submitError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Link
                to="/client/backups"
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                data-testid="create-backup-cancel"
              >
                Cancel
              </Link>
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={!canSubmit}
                data-testid="create-backup-submit"
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium"
              >
                {submitting ? 'Creating…' : 'Start backup'}
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  )
}
